import { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { fetchClientsForMentions, labelClientsForPicker } from "../services/clients.service";

export interface PickedClient {
  clientId: string;
  label: string;
}

interface PickerOption {
  id: string;
  label: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const INITIAL_LIMIT = 20;

/**
 * Lets an adviser pick a REAL client to test a workflow against, alongside
 * RunWithMeetingPicker's meeting picker — some workflows (e.g. a chain that
 * never touches a meeting) only need a clientId in the trigger context, not a
 * meetingId.
 *
 * Searches server-side as the advisor types (debounced) instead of fetching
 * the whole org and filtering locally — this org's client list is confirmed
 * to run into the hundreds, which made the old fetch-everything-once
 * approach both slow and prone to silently missing results.
 */
export default function RunWithClientPicker({
  value,
  onChange,
  helperText,
}: {
  value: PickedClient | null;
  onChange: (client: PickedClient | null) => void;
  helperText?: string;
}) {
  const [options, setOptions] = useState<PickerOption[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const run = () => {
      setLoading(true);
      setLoadError(false);
      fetchClientsForMentions(inputValue.trim() || undefined, inputValue.trim() ? 50 : INITIAL_LIMIT)
        .then((clients: any) => {
          if (cancelled) return;
          setOptions(labelClientsForPicker(clients || []));
        })
        .catch(() => {
          if (!cancelled) {
            setOptions([]);
            setLoadError(true);
          }
        })
        .finally(() => !cancelled && setLoading(false));
    };

    debounceRef.current = setTimeout(run, inputValue ? SEARCH_DEBOUNCE_MS : 0);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  const selected = value ? { id: value.clientId, label: value.label } : null;

  return (
    <Autocomplete
      size="small"
      loading={loading}
      options={options}
      value={selected}
      onInputChange={(_, next, reason) => {
        if (reason === "input") setInputValue(next);
      }}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={(o) => o}
      onChange={(_, next) => onChange(next ? { clientId: next.id, label: next.label } : null)}
      sx={{ flex: 1, minWidth: 180 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Test with client (optional)"
          placeholder="Type to search…"
          title={selected?.label}
          helperText={loadError ? "Couldn't load clients — try again" : helperText}
          error={loadError}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
